<template>
    <div v-if="withButton">
        <b-form-group v-bind="$attrs">
            <b-button variant="primary" @click="editor.show = true"> {{ btnLabel }} </b-button>
        </b-form-group>

        <template v-if="sidebar">
            <b-sidebar right v-model="editor.show">
                <b-form-group :description="editor_description">
                    <MonacoEditor 
                        ref="monacoEditor"
                        v-model="content" 
                        :language="monacoLanguage"
                        :options="monacoEditorOptions"
                        :readOnly="$attrs.readOnly"
                        :placeholder="placeholder"
                        :height="height"
                        :showFullscreenButton="showFullscreenButton"
                        :title="editorTitle"
                        :typeDefinitions="editorTypeDefinitions"
                        :enableProtokolTypes="editorEnableProtokolTypes"
                        :functionSignature="editorFunctionSignature"
                        :scopeDeclarations="editorScopeDeclarations"
                        @ready="editorReady"
                        @input="onChange"
                        @fullscreen-change="onFullscreenChange"
                    />
                    <small v-for="(error, index) in errors" :key="index">  {{ error }}  </small>
                </b-form-group>
                <slot></slot>
            </b-sidebar>
        </template>

        <template v-else>
            <main-dialog :title="$attrs.label" size="xl" v-model="editor.show" hide-footer>
                <b-form-group :description="editor_description">
                    <MonacoEditor 
                        ref="monacoEditor"
                        v-model="content" 
                        :language="monacoLanguage"
                        :options="monacoEditorOptions"
                        :readOnly="$attrs.readOnly"
                        :placeholder="placeholder"
                        :height="height"
                        :showFullscreenButton="showFullscreenButton"
                        :title="editorTitle"
                        :typeDefinitions="editorTypeDefinitions"
                        :enableProtokolTypes="editorEnableProtokolTypes"
                        :functionSignature="editorFunctionSignature"
                        :scopeDeclarations="editorScopeDeclarations"
                        @ready="editorReady"
                        @input="onChange"
                        @fullscreen-change="onFullscreenChange"
                    />
                    <small v-for="(error, index) in errors" :key="index">  {{ error }}  </small>
                </b-form-group>
                <slot></slot>
            </main-dialog>
        </template>
        
    </div>
    <div v-else>
        <b-form-group :description="editor_description">
            <MonacoEditor 
                ref="monacoEditor"
                v-model="content" 
                :language="monacoLanguage"
                :options="monacoEditorOptions"
                :readOnly="$attrs.readOnly"
                :placeholder="placeholder"
                :height="height"
                :showFullscreenButton="showFullscreenButton"
                :title="editorTitle"
                :typeDefinitions="editorTypeDefinitions"
                :enableProtokolTypes="editorEnableProtokolTypes"
                :functionSignature="editorFunctionSignature"
                :scopeDeclarations="editorScopeDeclarations"
                @ready="editorReady"
                @input="onChange"
                @fullscreen-change="onFullscreenChange"
            />
            <small v-for="(error, index) in errors" :key="index">  {{ error }}  </small>
        </b-form-group>
        <slot></slot>
    </div>
</template>

<style scoped>
/* Simple styles for the Code editor wrapper */
</style>

<script>
// Monaco Editor imports
import MonacoEditor from './MonacoEditor'

export default {
    name: "Code",
    components: {
        MonacoEditor
    },
    props: {
        value: {
            type: String
        },
        placeholder: {
            type: String
        },
        editor_description: {
            type: String
        },
        errors: {
            type: Array,
            default: function() {
                return []
            }
        },
        btnLabel: {
            type: String,
            default: 'Open editor'
        },
        withButton: {
            type: Boolean,
            default: true
        },
        sidebar: {
            type: Boolean,
            default: false
        },
        options: {
            type: Object,
            default: () => ({})
        },
        height: {
            type: Number,
            default: 300
        },
        title: {
            type: String,
            default: ''
        },
        typeDefinitions: {
            type: [Object, Array],
            default: () => ({})
        },
        enableProtokolTypes: {
            type: Boolean,
            default: false
        },
        functionSignature: {
            type: Object,
            default: null
        },
        scopeDeclarations: {
            type: String,
            default: null
        },
        lang: {
            type: String,
            default: ''
        }
    },
    data : function() {
        return {
            loadEditor: false,
            editor: {
                show: false,
            }
        }
    },
    computed: {
        content: {
            get() {
                return this.value;
            },

            set(val) {
                this.$emit('input', val)
            }
        },

        // Convert CodeMirror mode to Monaco language
        monacoLanguage() {
            const source = (this.options && this.options.mode) || this.lang || '';
            if (source) {
                const languageMap = {
                    'javascript': 'javascript',
                    'text/javascript': 'javascript',
                    'json': 'json',
                    'application/json': 'json',
                    'html': 'html',
                    'text/html': 'html',
                    'css': 'css',
                    'text/css': 'css',
                    'typescript': 'typescript',
                    'sql': 'sql',
                    'text/x-sql': 'sql'
                };
                return languageMap[source] || 'javascript';
            }
            return 'javascript';
        },

        showFullscreenButton() {
            // Get showFullscreenButton from options, default to true
            return this.options && this.options.hasOwnProperty('showFullscreenButton') 
                ? this.options.showFullscreenButton 
                : true;
        },

        editorTitle() {
            // Priority: direct prop > options.title > empty string
            if (this.title) {
                return this.title;
            }
            if (this.options && this.options.title) {
                return this.options.title;
            }
            return '';
        },

        editorTypeDefinitions() {
            // Priority: direct prop > options.typeDefinitions > empty object
            if (this.typeDefinitions && Object.keys(this.typeDefinitions).length > 0) {
                return this.typeDefinitions;
            }
            if (this.options && this.options.typeDefinitions) {
                return this.options.typeDefinitions;
            }
            return {};
        },

        editorEnableProtokolTypes() {
            // Priority: direct prop > options.enableProtokolTypes > false
            if (this.enableProtokolTypes !== undefined) {
                return this.enableProtokolTypes;
            }
            if (this.options && this.options.hasOwnProperty('enableProtokolTypes')) {
                return this.options.enableProtokolTypes;
            }
            return false;
        },

        editorFunctionSignature() {
            // Priority: direct prop > options.functionSignature > null
            if (this.functionSignature !== null && this.functionSignature !== undefined) {
                return this.functionSignature;
            }
            if (this.options && this.options.functionSignature) {
                return this.options.functionSignature;
            }
            return null;
        },

        editorScopeDeclarations() {
            // Priority: direct prop > options.scopeDeclarations > null
            if (this.scopeDeclarations) {
                return this.scopeDeclarations;
            }
            if (this.options && this.options.scopeDeclarations) {
                return this.options.scopeDeclarations;
            }
            return null;
        },

        monacoEditorOptions() {
            return {
                theme: this.platformTheme() === 'light' ? 'vs' : 'vs-dark',
                readOnly: this.$attrs.readOnly || false,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
                lineNumbers: 'on',
                folding: true,
                wordWrap: 'on',
                automaticLayout: true
            };
        }
    },
    methods: {
        onChange(code) {
            this.$emit("onInput", code);
        },

        editorReady(editor) {
            this.loadEditor = true
            this.$emit('ready', editor);
        },

        onFullscreenChange(isFullscreen) {
            this.$emit('fullscreen-change', isFullscreen);
        },

        platformTheme() {
            if (this.$store) {
                return this.$store.getters.getCurrentTheme == 'light' ? 'light' : 'dark'
            }

            return 'dark'
        }
    }
}
</script>